import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Optional, Output, SimpleChanges } from '@angular/core';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { FormArray, FormGroup } from '@angular/forms';
import { SmartForm } from '../../../core/forms/smart-form';
import { FinanceTaxRecord } from '../../../core/services/finance.service';
import { InventoryProduct, InventoryProductVariant } from '../../../apps/inventory/products/products/products.type';
import { AutocompleteOption } from '../../inputs/autocomplete-select/autocomplete-select.component';
import { ProductItemOption } from '../../inputs/product-item-picker/product-item-picker';
import { InventoryService } from '../../../core/services/inventory.service';
import { AnalyticDistributionService } from '../../../core/services/analytic-distribution';
import { Subscription, debounceTime, firstValueFrom } from 'rxjs';

/** Presentation over the owner's real SmartForm: no duplicate line store or workflow API. */
@Component({
  selector: 'app-product-lines',
  standalone: false,
  templateUrl: './product-lines.html',
  styleUrl: './product-lines.scss',
})
export class ProductLines implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) form!: SmartForm | FormGroup;
  @Input() mode: 'sales' | 'purchase' = 'sales';
  @Input() saleKind: 'normal' | 'rental' | 'subscription' = 'normal';
  @Input() isReadOnly = false;
  @Input() allowPriceEdit = true;
  @Input() discountControl = 'discount_percent';
  @Input() showDescription = false;
  @Input() requireProduct = true;
  @Input() requireVariant = true;
  @Input() showIndividualItems = true;
  @Input({ required: true }) locationId: string | null = null;
  @Input() productFilter: 'sales' | 'purchase' | 'rental' = 'sales';
  @Input() showAccount = false;
  @Input() accountOptions: AutocompleteOption[] = [];
  @Input() showScheduledDate = false;
  @Input() scheduledDateType: 'date' | 'datetime-local' = 'date';
  @Input() showUom = false;
  @Input() uomOptions: AutocompleteOption[] = [];
  @Input() editableUom = false;
  @Input() showDiscount = true;
  @Input() showTaxes = true;
  @Input() showAnalytics = true;
  @Input() showDeposit = false;
  productOptions: AutocompleteOption[] = [];
  @Input() productById: ReadonlyMap<string, InventoryProduct> = new Map();
  catalogueError = '';
  analyticAccountsAvailable = false;
  private readonly formSubscription = new Subscription();
  private balanceCheckVersion = 0;
  @Input() taxes: FinanceTaxRecord[] = [];
  @Input() defaultTaxIds: string[] = [];
  @Input() partnerId: string | null = null;
  @Input() currency = 'TZS';
  @Input() productCreateRoute = '/inventory/products/new';
  @Input() untaxedTotal = 0;
  @Input() taxTotal = 0;
  @Input() grandTotal = 0;
  @Input() orderDiscountAmount = 0;
  @Input() shippingAmount = 0;
  @Input() shippingTaxAmount = 0;
  @Output() addLine = new EventEmitter<'product' | 'section' | 'note'>();
  @Output() removeLine = new EventEmitter<number>();
  @Output() reorderLine = new EventEmitter<CdkDragDrop<FormGroup[]>>();
  @Output() variantSelected = new EventEmitter<{ index: number; variant: InventoryProductVariant | null }>();
  @Output() productsLoaded = new EventEmitter<InventoryProduct[]>();

  constructor(
    @Optional() private readonly inventory?: InventoryService,
    @Optional() private readonly analyticDistributions?: AnalyticDistributionService,
  ) {}

  ngOnInit(): void {
    this.formSubscription.add(this.ownerForm.valueChanges.subscribe(() => {
      if (this.mode !== 'purchase' && !this.isReadOnly) {
        this.lineGroups.filter(line => this.lineType(line) === 'product').forEach(line =>
          this.setStockError(line, 'Checking stock at the selected location…'));
      }
    }));
    this.formSubscription.add(this.ownerForm.valueChanges.pipe(debounceTime(180)).subscribe(() => {
      void this.checkLineAvailability();
    }));
    void this.loadProducts();
    void this.loadAnalyticAvailability();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['productFilter'] && !changes['productFilter'].firstChange) void this.loadProducts();
    if (changes['locationId'] && !changes['locationId'].firstChange) {
      for (let index = 0; index < this.lineGroups.length; index++) {
        if (!this.productUsesIndividualItems(index)) continue;
        const line = this.lineGroups[index];
        line.get('product_item_id')?.setValue('', { emitEvent: false });
        line.get('product_variant_id')?.setValue('', { emitEvent: false });
        line.get('attribute_value_ids')?.setValue([], { emitEvent: false });
      }
      void this.checkLineAvailability();
    }
  }

  ngOnDestroy(): void {
    this.balanceCheckVersion++;
    this.formSubscription.unsubscribe();
  }

  private eligible(product: InventoryProduct): boolean {
    if (this.productFilter === 'rental') return !!product.is_rental_product && !!product.can_be_sold;
    return this.productFilter === 'purchase' ? !!product.can_be_purchased : !!product.can_be_sold;
  }

  async loadProducts(): Promise<void> {
    if (!this.inventory) return;
    try {
      const products: InventoryProduct[] = [];
      let page = 1;
      let totalPages = 1;
      do {
        const batch = await this.inventory.getProductsList({ status: 'active', page, per_page: 100 });
        products.push(...batch.products.filter(product => this.eligible(product)));
        totalPages = batch.meta.total_pages;
        page++;
      } while (page <= totalPages);
      const shared = this.productById as Map<string, InventoryProduct>;
      products.forEach(product => shared.set(product.id, product));
      this.productOptions = products.map(product => ({
        value: product.id,
        label: product.internal_reference ? `${product.name} [${product.internal_reference}]` : product.name,
      }));
      this.catalogueError = '';
      this.productsLoaded.emit(products);
      void this.checkLineAvailability();
    } catch {
      this.catalogueError = 'Products could not be loaded. Retry before saving.';
    }
  }

  readonly searchProductOptions = (query: string): AutocompleteOption[] => {
    const search = query.trim().toLocaleLowerCase();
    return search ? this.productOptions.filter(option => option.label.toLocaleLowerCase().includes(search)) : this.productOptions;
  };

  readonly preloadProductOptions = async (ids: string[]): Promise<AutocompleteOption[]> => {
    const missing = ids.filter(id => id && !this.productById.has(id));
    if (missing.length && this.inventory) {
      await Promise.all(missing.map(async id => {
        const product = await this.inventory!.getProductById(id);
        if (product) (this.productById as Map<string, InventoryProduct>).set(id, product);
      }));
    }
    return ids.map(id => {
      const product = this.productById.get(id);
      return product ? { value: id, label: product.name } : null;
    }).filter((option): option is AutocompleteOption => !!option);
  };

  private async loadAnalyticAvailability(): Promise<void> {
    if (!this.analyticDistributions || !this.showAnalytics) return;
    try {
      const options = await firstValueFrom(this.analyticDistributions.options());
      this.analyticAccountsAvailable = (options.analytic_accounts ?? []).length > 0;
    } catch {
      this.analyticAccountsAvailable = this.lineGroups.some(line => Object.keys(line.get('analytic_distribution')?.value ?? {}).length > 0);
    }
  }

  get itemColumnVisible(): boolean {
    return this.showIndividualItems && this.productOptions.some(option => {
      const tracking = this.productById.get(option.value)?.tracking;
      return tracking === 'by_unique_item' || tracking === 'by_serial_number';
    });
  }

  get analyticColumnVisible(): boolean {
    return this.showAnalytics && (this.analyticAccountsAvailable ||
      this.lineGroups.some(line => Object.keys(line.get('analytic_distribution')?.value ?? {}).length > 0));
  }

  private setStockError(line: FormGroup, message: string | null): void {
    const errors = { ...(line.errors ?? {}) };
    delete errors['stockAvailability'];
    if (message) errors['stockAvailability'] = message;
    line.setErrors(Object.keys(errors).length ? errors : null);
  }

  async checkLineAvailability(): Promise<void> {
    const version = ++this.balanceCheckVersion;
    const lines = this.lineGroups.filter(line => this.lineType(line) === 'product');
    if (this.mode === 'purchase' || this.isReadOnly || !this.inventory) {
      lines.forEach(line => this.setStockError(line, null));
      return;
    }
    const candidates = lines.map(line => ({
      line, product: this.productById.get(String(line.get('product_id')?.value || '')),
      variantId: String(line.get('product_variant_id')?.value || ''),
      itemId: String(line.get('product_item_id')?.value || ''),
      quantity: Number(line.get('quantity')?.value || 0),
    })).filter(candidate => candidate.product && candidate.product.product_type !== 'service');
    lines.forEach(line => this.setStockError(line, null));
    if (!candidates.length) return;
    if (!this.locationId) {
      candidates.forEach(({ line }) => this.setStockError(line, 'Choose a stock location before selecting inventory.'));
      return;
    }
    const variantIds = [...new Set(candidates.filter(candidate => !candidate.itemId && candidate.variantId)
      .map(candidate => candidate.variantId))];
    const itemIds = [...new Set(candidates.filter(candidate => !!candidate.itemId).map(candidate => candidate.itemId))];
    candidates.forEach(({ line }) => this.setStockError(line, 'Checking stock at the selected location…'));
    try {
      const result = await this.inventory.checkBalances(this.locationId, variantIds, itemIds);
      if (version !== this.balanceCheckVersion) return;
      const variantBalances = new Map(result.variants.map(balance => [balance.variant_id, Number(balance.available)]));
      const itemBalances = new Map(result.items.map(balance => [balance.item_id, balance]));
      const requested = new Map<string, number>();
      const usedItems = new Set<string>();
      for (const candidate of candidates) {
        const { line, product, variantId, itemId, quantity } = candidate;
        let error: string | null = null;
        if (!variantId || !Number.isFinite(quantity) || quantity <= 0) {
          error = 'Choose a variant and a positive quantity.';
        } else if (this.productUsesIndividualItems(this.lineGroups.indexOf(line))) {
          const balance = itemBalances.get(itemId);
          if (!itemId || !balance || balance.product_id !== product!.id || balance.variant_id !== variantId
            || Number(balance.available) < 1 || quantity !== 1 || usedItems.has(itemId)) {
            error = 'Choose one available item at the selected location.';
          }
          usedItems.add(itemId);
        } else {
          const total = (requested.get(variantId) ?? 0) + quantity;
          requested.set(variantId, total);
          if (total > (variantBalances.get(variantId) ?? 0))
            error = `Insufficient stock for this variant at the selected location (${variantBalances.get(variantId) ?? 0} available).`;
        }
        this.setStockError(line, error);
      }
    } catch {
      if (version === this.balanceCheckVersion)
        candidates.forEach(({ line }) => this.setStockError(line, 'Stock could not be verified. Retry before saving.'));
    }
  }

  get ownerForm(): FormGroup { return this.form instanceof FormGroup ? this.form : this.form.form; }
  get columnCount(): number {
    return 8 + Number(this.showTaxes) + Number(this.analyticColumnVisible) + Number(this.showDeposit)
      + Number(this.showDiscount) + Number(this.itemColumnVisible) + Number(this.showDescription)
      + Number(this.showAccount) + Number(this.showScheduledDate) + Number(this.showUom);
  }
  get lineGroups(): FormGroup[] { return (this.ownerForm.get('lines') as FormArray).controls as FormGroup[]; }
  lineType(line: FormGroup): 'product' | 'section' | 'note' {
    const kind = line.get('line_type')?.value;
    return kind === 'section' || kind === 'note' ? kind : 'product';
  }
  productForLine(index: number): InventoryProduct | null {
    return this.productById.get(String(this.lineGroups[index]?.get('product_id')?.value || '')) || null;
  }
  productUsesIndividualItems(index: number): boolean {
    const tracking = this.productForLine(index)?.tracking;
    return tracking === 'by_unique_item' || tracking === 'by_serial_number';
  }
  onItemSelected(index: number, item: ProductItemOption | null): void {
    const line = this.lineGroups[index];
    if (!line) return;
    if (!item) {
      line.get('product_variant_id')?.setValue('', { emitEvent: false });
      line.get('attribute_value_ids')?.setValue([], { emitEvent: false });
      return;
    }
    const variant = this.productForLine(index)?.variants.find(candidate => candidate.id === item.product_variant_id);
    if (!variant) {
      line.get('product_item_id')?.setErrors({ productItemUnavailable: 'The item variant is unavailable.' });
      return;
    }
    line.get('product_variant_id')?.setValue(variant.id, { emitEvent: false });
    line.get('attribute_value_ids')?.setValue(variant.attribute_value_ids ?? [], { emitEvent: false });
    this.variantSelected.emit({ index, variant });
    // Some owners refresh the variant price when notified. Preserve the item
    // after that refresh because its variant is the canonical selection.
    line.get('product_item_id')?.setValue(item.id, { emitEvent: false });
    line.get('quantity')?.setValue(1);
  }
  selectedValueIds(index: number): string[] { return this.lineGroups[index]?.get('attribute_value_ids')?.value || []; }
  onLineAttributeSelection(index: number, values: string[]): void {
    this.lineGroups[index]?.get('attribute_value_ids')?.setValue(values, { emitEvent: false });
  }
  moneyAmount(value: unknown): number { return Number(Array.isArray(value) ? value[0] : value) || 0; }
  asCurrency(value: unknown): string {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: this.currency, maximumFractionDigits: 2 }).format(Number(value) || 0);
  }
}
